import { CSSProperties } from 'react';

import styled from '@emotion/styled';
import ReactHotToast, { ToastOptions as BaseToastOptions, ToastPosition } from 'react-hot-toast';

import { IcoCheck, IcoError, IcoInformation } from '@myrealtrip/web-icons';
import { ToastTemplate } from '@myrealtrip/web-ui';

import { DESKTOP_PRIMARY_CONTAINER_WIDTH } from '~constants/common';

type ToastTemplateProps = React.ComponentProps<typeof ToastTemplate>;

type PickedToastTemplateProps = Pick<
  ToastTemplateProps,
  'description' | 'actionTitle' | 'onActionClick' | 'icon' | 'actionClickArea' | 'verticalOffset'
>;

interface ToastOptions extends BaseToastOptions {
  delay?: number;
}

export interface Options extends PickedToastTemplateProps {
  message: string;
  id?: string;

  duration?: ToastOptions['duration'];
  position?: ToastOptions['position'];
  toastOffsetCssVarName?: string;
  /**
   * @description 기본은 setTimeout(toast, delay)이며, true를 전달할 경우 setTimeout(toast, 0)으로 설정됩니다.
   */
  delay?: boolean | number;
  titleLineClamp?: number;
}

type PositionDirection = 'top' | 'bottom';

function getPositionDirection(position: ToastPosition): PositionDirection | null {
  const [direction] = position.split('-');
  const allowDirections = ['top', 'bottom'];
  if (!allowDirections.some((allowDiretion) => allowDiretion === direction)) {
    return null;
  }

  return direction as PositionDirection;
}

function getOffsetStyle({
  position,
  toastOffsetCssVarName,
  verticalOffset,
}: {
  position: ToastPosition;
  toastOffsetCssVarName?: string;
  verticalOffset?: number;
}): CSSProperties {
  switch (getPositionDirection(position)) {
    case 'top':
      return {
        top: verticalOffset ? `${verticalOffset}px` : `var(${toastOffsetCssVarName})`,
      };
    case 'bottom':
      return {
        bottom: verticalOffset ? `${verticalOffset}px` : `var(${toastOffsetCssVarName})`,
      };
    default:
      return {};
  }
}

function getDelayOption(delay?: boolean | number) {
  if (typeof delay === 'number') {
    return delay;
  }

  return delay ? 0 : undefined;
}

export const commonToast = {
  success,
  neutral,
  warning,
  error,
  blank,
};

function getToastOptions(options: Options): {
  componentOptions: ToastTemplateProps;
  toastOptions: ToastOptions;
} {
  const {
    message,
    id,
    icon = <IcoCheck />,
    description,
    actionTitle,
    onActionClick,
    actionClickArea,
    verticalOffset,
    position = 'top-center',
    toastOffsetCssVarName,
    delay,
    duration,
    titleLineClamp,
  } = options;

  return {
    componentOptions: {
      title: message,
      icon,
      shape: 'default',
      description,
      actionTitle,
      actionClickArea,
      verticalOffset,
      onActionClick,
      titleLineClamp,
    },
    toastOptions: {
      id,
      position,
      duration: duration ?? 15000,
      style: {
        maxWidth: DESKTOP_PRIMARY_CONTAINER_WIDTH,
        ...getOffsetStyle({ position, toastOffsetCssVarName, verticalOffset }),
      },
      delay: getDelayOption(delay),
    },
  };
}

function withDelay(toast: () => void, delay?: number) {
  if (typeof delay === 'number') {
    setTimeout(toast, delay);
    return;
  }

  toast();
}

function success(options: Options) {
  const { componentOptions, toastOptions } = getToastOptions(options);

  withDelay(
    () => ReactHotToast(<Toast {...componentOptions} type="success" />, toastOptions),
    toastOptions.delay,
  );
}

function error(options: Options) {
  const { componentOptions, toastOptions } = getToastOptions({ icon: <IcoError />, ...options });

  withDelay(
    () => ReactHotToast(<Toast {...componentOptions} type="error" />, toastOptions),
    toastOptions.delay,
  );
}

function warning(options: Options) {
  const { componentOptions, toastOptions } = getToastOptions({
    icon: <IcoInformation />,
    ...options,
  });

  withDelay(
    () => ReactHotToast(<Toast {...componentOptions} type="warning" />, toastOptions),
    toastOptions.delay,
  );
}

function neutral(options: Options) {
  const { componentOptions, toastOptions } = getToastOptions({
    icon: <IcoCheck />,
    ...options,
  });

  withDelay(
    () => ReactHotToast(<Toast {...componentOptions} type="neutral" />, toastOptions),
    toastOptions.delay,
  );
}

function blank(options: Options) {
  const { componentOptions, toastOptions } = getToastOptions({
    ...options,
  });

  withDelay(
    () => ReactHotToast(<Toast {...componentOptions} type="blank" />, toastOptions),
    toastOptions.delay,
  );
}

const Toast = styled(ToastTemplate)`
  width: 100% !important;
  max-width: 100% !important;
`;
